// Stores
import { useResult } from "../../../../../shared/formContext/hooks/useResult";

// Components
import ChartLine from "../../../../../components/ChartLine/ChartLine.js";
import ChartLegend from "../../../../../components/ChartLegend/ChartLegend.js";

// Utils
import { utils } from "../../../../../helpers/index.js";

// WP react stuff
import { useState, useEffect } from "@wordpress/element";
import { __, sprintf } from "@wordpress/i18n";

interface Installment {
    date: string;
    principalPaid: number;
    interestPaid: number;
    remainingBalance: number;
}

/**
 * Month-by-month amortization chart. Mirrors mortgage-payment's
 * `TabAmortizacao` (same legends, same "Mês N" tooltip format), reading from
 * the flat `results.installments` array `QuotePaymentService` computes locally
 * (see the note there — the external API doesn't return a schedule yet).
 */
const ChartView: React.FC = () => {
    const { resultData } = useResult();
    const { response } = resultData;
    const [legends, setLegends] = useState<Record<string, { value: number; color: string }>>();
    const [chartData, setChartData] = useState<any>();

    useEffect(() => {
        const installments: Installment[] = response?.data?.result?.results?.installments || [];
        if (installments.length === 0) {
            setChartData(undefined);
            setLegends(undefined);
            return;
        }

        let totalPrincipalPaid = 0;
        let totalInterestPaid = 0;

        setLegends({
            [__("Capital pago", "df-simulators")]: {
                value: 0,
                color: "#CCEBF5",
            },
            [__("Juros pagos", "df-simulators")]: {
                value: 0,
                color: "#09C",
            },
            [__("Dívida", "df-simulators")]: {
                value: installments[0]?.remainingBalance || 0,
                color: "#000031",
            },
        });

        setChartData({
            labels: installments.map((item) => item.date),
            tooltip: {
                title: (tooltipItem: any) =>
                    sprintf(
                        /* translators: 1: month number, 2: formatted date */
                        __("Mês %1$d\n%2$s", "df-simulators"),
                        tooltipItem[0].dataIndex + 1,
                        utils.transformDate(tooltipItem[0].label, false, false, "-"),
                    ),
                label: () => false,
                callbackOnShow: (tooltipItem: any) => {
                    const key = tooltipItem.dataset.label;

                    setLegends((prevLegends) => ({
                        ...prevLegends,
                        [key]: {
                            ...(prevLegends?.[key] || { color: "" }),
                            value: tooltipItem.raw,
                        },
                    }));
                },
            },
            dataSets: [
                {
                    label: __("Capital pago", "df-simulators"),
                    data: installments.map((item) => {
                        totalPrincipalPaid += item.principalPaid || 0;
                        return totalPrincipalPaid;
                    }),
                    color: "#CCEBF5",
                },
                {
                    label: __("Juros pagos", "df-simulators"),
                    data: installments.map((item) => {
                        totalInterestPaid += item.interestPaid || 0;
                        return totalInterestPaid;
                    }),
                    color: "#09C",
                },
                {
                    label: __("Dívida", "df-simulators"),
                    data: installments.map((item) => item.remainingBalance),
                    color: "#000031",
                },
            ],
        });
    }, [response]);

    return (
        <div className="tab-amortizacao">
            {chartData && <ChartLine data={chartData} />}
            {legends && (
                <div className="mt-4 pt-4">
                    <ChartLegend data={legends} horizontal />
                </div>
            )}
        </div>
    );
};

export default ChartView;
